<template>
  <div class="relative inline-flex items-center">
    <!-- Info Icon -->
    <button
      ref="triggerRef"
      type="button"
      class="text-gray-400 hover:text-gray-600 transition-colors focus:outline-none"
      @mouseenter="openTooltip"
      @mouseleave="scheduleClose"
      @click.prevent="show = !show"
    >
      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
      </svg>
    </button>

    <!-- Tooltip Panel -->
    <Teleport to="body">
      <div
        v-if="show"
        ref="tooltipRef"
        class="fixed z-[9999] w-[420px] max-w-[90vw] bg-white border border-gray-200 rounded-lg shadow-xl p-5 text-sm text-gray-700 leading-relaxed"
        :style="tooltipStyle"
        @mouseenter="cancelClose"
        @mouseleave="scheduleClose"
      >
        <!-- German Content -->
        <template v-if="locale === 'de'">
          <p class="mb-3">
            Ab sofort werden die Rabattstufen anhand des <strong>gesamten Warenkorbwerts</strong> berechnet, nicht mehr pro Einzelprodukt. Kurz gesagt: Je mehr ihr insgesamt bestellt, desto besser wird der Preis für <strong>alle</strong> Produkte, egal wie ihr euren Warenkorb zusammenstellt. Das bedeutet für euch:
          </p>
          <ul class="list-disc pl-5 space-y-1.5 mb-3">
            <li><strong>Keine Mindestbestellmengen pro Produkt</strong>: Bestellt jedes Produkt in der Menge, die ihr wirklich braucht.</li>
            <li><strong>Freie Zusammenstellung</strong>: Kombiniert verschiedene Produkte und Mengen ganz nach Bedarf.</li>
            <li><strong>Jeder Artikel zählt</strong>: Alle Produkte im Warenkorb fließen gemeinsam in den Gesamtwert zur Berechnung der Rabattstufe ein.</li>
            <li><strong>Rabatt gilt für gesamten Warenkorb</strong>: Sobald der Gesamtwert die nächste Stufe erreicht, profitiert ihr automatisch bei <strong>allen Produkten</strong> im Warenkorb vom besseren Preis.</li>
          </ul>
          <p class="text-xs text-gray-500">
            <strong>Tipp</strong>: In der Warenkorb-Ansicht (auf der rechten Seite) seht ihr jederzeit den Fortschritt zur nächsten Rabattstufe. So wisst ihr immer genau, wie viel euch noch bis zum nächsten Preisvorteil fehlt.
          </p>
        </template>

        <!-- English Content -->
        <template v-else>
          <p class="mb-3">
            From now on, discount tiers are calculated based on the <strong>total cart value</strong>, no longer per individual product. In short: The more you order in total, the better the price on <strong>all</strong> products, regardless of how you assemble your cart. This means for you:
          </p>
          <ul class="list-disc pl-5 space-y-1.5 mb-3">
            <li><strong>No minimum order quantities per product</strong>: Order each product in the quantity you really need.</li>
            <li><strong>Free assembly</strong>: Mix and match different products and quantities as needed.</li>
            <li><strong>Every item counts</strong>: All products in the cart contribute to the total value used to calculate the discount tier.</li>
            <li><strong>Discount applies to the entire cart</strong>: As soon as the total value reaches the next tier, you automatically benefit from the better price on <strong>all products</strong> in the cart.</li>
          </ul>
          <p class="text-xs text-gray-500">
            <strong>Tip</strong>: In the cart view (on the right side) you can see at any time how close you are to the next discount tier. That way you always know exactly how much you still need until the next price advantage.
          </p>
        </template>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onUnmounted } from 'vue'
import { getCurrentLocale } from '@/utils/localization'

const show = ref(false)
const triggerRef = ref<HTMLElement | null>(null)
const tooltipRef = ref<HTMLElement | null>(null)
const tooltipStyle = ref<Record<string, string>>({})
let closeTimer: number | null = null

const locale = computed(() => getCurrentLocale())

const openTooltip = (): void => {
  cancelClose()
  show.value = true
}

const scheduleClose = (): void => {
  closeTimer = window.setTimeout(() => {
    show.value = false
  }, 150)
}

const cancelClose = (): void => {
  if (closeTimer) {
    clearTimeout(closeTimer)
    closeTimer = null
  }
}

watch(show, async (visible) => {
  if (!visible) return
  await nextTick()
  positionTooltip()
})

const positionTooltip = (): void => {
  if (!triggerRef.value || !tooltipRef.value) return

  const rect = triggerRef.value.getBoundingClientRect()
  const tooltipWidth = Math.min(420, window.innerWidth * 0.9)
  const tooltipHeight = tooltipRef.value.offsetHeight
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  let top = rect.bottom + 8
  let left = rect.left + rect.width / 2 - tooltipWidth / 2

  if (left < 8) {
    left = 8
  }
  if (left + tooltipWidth > viewportWidth - 8) {
    left = viewportWidth - tooltipWidth - 8
  }

  if (top + tooltipHeight > viewportHeight - 8) {
    top = rect.top - tooltipHeight - 8
  }

  tooltipStyle.value = {
    top: `${top}px`,
    left: `${left}px`,
  }
}

onUnmounted(() => {
  cancelClose()
})
</script>
